import { useEffect, useMemo } from "react";
import ReactWebChat, { createStyleSet } from "botframework-webchat";
import { RotateCcw, X } from "lucide-react";
import { useAgent } from "../hooks/useAgent";
import type { Theme } from "../hooks/useTheme";

type Props = {
  open: boolean;
  onClose: () => void;
  theme: Theme;
  userId: string;
  userName: string;
};

export function ChatPanel({ open, onClose, theme, userId, userName }: Props) {
  const { directLine, loading, error, reset } = useAgent(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Mirrors this app's actual theme tokens (see styles/app.css :root /
  // [data-theme="dark"]) rather than arbitrary colors — createStyleSet needs
  // literal color strings (it does its own hover/derived-shade math on
  // `accent`), so we mirror the values here instead of passing var(--x).
  const styleOptions = useMemo(() => {
    const isDark = theme === "dark";
    return {
      bubbleBackground: isDark ? "rgba(22, 30, 45, 0.78)" : "#F1F5F9",
      bubbleTextColor: isDark ? "#E6EDF3" : "#1A1F2E",
      // Brand navy — matches .btn-primary's fill elsewhere in the app.
      bubbleFromUserBackground: isDark ? "#4A90C2" : "#003057",
      bubbleFromUserTextColor: "#ffffff",
      bubbleBorderRadius: 12,
      bubbleFromUserBorderRadius: 12,
      bubbleMaxWidth: 360,
      sendBoxBackground: isDark ? "rgba(22, 30, 45, 0.95)" : "#ffffff",
      sendBoxTextColor: isDark ? "#E6EDF3" : "#1A1F2E",
      sendBoxBorderTop: isDark
        ? "1px solid #1E293B"
        : "1px solid #E5E7EB",
      backgroundColor: "transparent",
      rootHeight: "100%",
      rootWidth: "100%",
      hideUploadButton: true,
      botAvatarInitials: "AI",
      userAvatarInitials: initials(userName),
      // Brookfield warm gold accent — matches --accent in app.css.
      accent: isDark ? "#E0C588" : "#C9A961",
      // Sample-question ("suggested actions") pills — WebChat's own defaults
      // use a stock blue here that isn't derived from `accent`, so it's set
      // explicitly to match the site's gold/navy palette in every state.
      suggestedActionBackgroundColor: "transparent",
      suggestedActionBorderColor: isDark ? "#E0C588" : "#C9A961",
      suggestedActionTextColor: isDark ? "#4A90C2" : "#003057",
      suggestedActionBackgroundColorOnHover: isDark
        ? "rgba(224, 197, 136, 0.18)"
        : "rgba(201, 169, 97, 0.12)",
      suggestedActionBorderColorOnHover: isDark ? "#E0C588" : "#C9A961",
      suggestedActionTextColorOnHover: isDark ? "#4A90C2" : "#003057",
      suggestedActionBackgroundColorOnActive: isDark
        ? "rgba(224, 197, 136, 0.28)"
        : "rgba(201, 169, 97, 0.22)",
      suggestedActionBorderColorOnActive: isDark ? "#E0C588" : "#C9A961",
      suggestedActionTextColorOnActive: isDark ? "#4A90C2" : "#003057",
      suggestedActionBackgroundColorOnFocus: isDark
        ? "rgba(224, 197, 136, 0.18)"
        : "rgba(201, 169, 97, 0.12)",
      suggestedActionBorderColorOnFocus: isDark ? "#E0C588" : "#C9A961",
      suggestedActionTextColorOnFocus: isDark ? "#4A90C2" : "#003057",
      primaryFont:
        '"Inter", "Segoe UI Variable", "Segoe UI", system-ui, sans-serif',
    };
  }, [theme, userName]);

  const styleSet = useMemo(() => createStyleSet(styleOptions), [styleOptions]);

  if (!open) return null;

  return (
    <>
      <div
        className="chat-backdrop"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="chat-panel"
        role="dialog"
        aria-label="AI assistant"
      >
        <header className="chat-panel-head">
          <div className="chat-panel-head-text">
            <div className="chat-panel-title">AI Assistant</div>
            <div className="chat-panel-subtitle">
              Ask about the AI Library, initiatives, or platforms
            </div>
          </div>
          <button
            type="button"
            className="chat-panel-icon-btn"
            onClick={reset}
            title="Start a new conversation"
            aria-label="Reset conversation"
            disabled={loading}
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            className="chat-panel-icon-btn"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="chat-panel-body">
          {loading ? (
            <div className="chat-panel-status">
              <div className="spinner" />
              <p>Starting the conversation…</p>
            </div>
          ) : error ? (
            <div className="chat-panel-status chat-panel-error">
              <p>{error}</p>
              <button className="btn btn-sm" onClick={reset}>
                Try again
              </button>
            </div>
          ) : directLine ? (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <ReactWebChat
              directLine={directLine as any}
              styleSet={styleSet}
              userID={userId}
              username={userName}
              locale="en-US"
            />
          ) : null}
        </div>
      </aside>
    </>
  );
}

function initials(name: string) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}
